import { Role } from "../config/panelTableConfig";

/**
 * 角色相关工具函数
 */

/**
 * 格式化角色数据
 */
export const formatRoleData = (rawRole: any): Role => {
  // 从 isActive 转换为 status 值
  let statusValue = "active";
  if (rawRole.isActive === false) {
    statusValue = "inactive";
  } else if (rawRole.status) {
    statusValue = rawRole.status;
  }

  return {
    ...rawRole,
    id: rawRole.id || rawRole._id || Math.random().toString(),
    key: rawRole.id || rawRole._id || Math.random().toString(),
    code: rawRole.code || "",
    name: rawRole.name || rawRole.roleName || rawRole.displayName || "未命名",
    status: statusValue,
  };
};

/**
 * 批量格式化角色数据
 */
export const formatRoleList = (rawRoles: any[]): Role[] => {
  if (!Array.isArray(rawRoles)) {
    return [];
  }

  return rawRoles.map(formatRoleData);
};

/**
 * 创建角色表单初始值
 */
export const createRoleInitialValues = (role: Role | null) => {
  if (role) {
    return {
      name: role.name || "",
      code: role.code || "",
      description: role.description || "",
      status: role.status || "active",
    };
  }

  // 新建角色时返回明确的空值，确保表单完全清空
  return {
    name: "",
    code: "",
    description: "",
    status: "active", // 默认状态为启用
  };
};

/**
 * 转换表单数据为API格式
 */
export const transformFormDataToApiFormat = (formData: any) => {
  // 确保角色编码是大写
  const codeValue = formData.code ? formData.code.toUpperCase() : "";

  return {
    name: formData.name,
    code: codeValue,
    description: formData.description,
    isActive: formData.status === "active",
  };
};

/**
 * 从API响应中提取角色列表
 */
export const extractRolesFromResponse = (response: any): any[] => {
  try {
    const responseData = response?.data;

    if (Array.isArray(responseData?.data)) {
      return responseData.data;
    } else if (Array.isArray(responseData?.items)) {
      return responseData.items;
    } else if (Array.isArray(responseData)) {
      return responseData;
    } else if (Array.isArray(response)) {
      return response;
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * 从API响应中提取分页信息
 */
export const extractPaginationFromResponse = (response: any, fallbackTotal: number = 0) => {
  const paginationData = response?.data;
  return {
    total: paginationData?.pagination?.total || paginationData?.total || fallbackTotal,
    current: paginationData?.pagination?.current || paginationData?.page || 1,
    pageSize: paginationData?.pagination?.pageSize || paginationData?.pageSize || 10,
  };
};

/**
 * 验证角色编码格式
 */
export const validateRoleCode = (code: string): boolean => {
  const pattern = /^[A-Z_-]+$/;
  return pattern.test(code);
};

/**
 * 生成搜索参数
 */
export const buildSearchParams = (searchValues: any) => {
  const params: Record<string, any> = {};

  // 处理角色名称
  if (searchValues.nameContains) {
    params.nameContains = searchValues.nameContains;
  }

  // 处理角色编码
  if (searchValues.idContains) {
    params.idContains = searchValues.idContains;
  }

  // 特别处理状态参数 - 空字符串表示"全部"
  if (searchValues.status !== undefined && searchValues.status !== "") {
    params.status = searchValues.status;
  }

  return params;
};

/**
 * 获取状态显示文本
 */
export const getStatusText = (status: string): string => {
  switch (status) {
    case "active":
      return "启用";
    case "inactive":
      return "禁用";
    default:
      return "未知";
  }
};

/**
 * 获取状态切换文本
 */
export const getToggleStatusText = (currentStatus: string): { newStatus: string; actionText: string } => {
  const newStatus = currentStatus === "active" ? "inactive" : "active";
  const actionText = newStatus === "active" ? "启用" : "禁用";

  return { newStatus, actionText };
};
